type FbqArguments = [string, ...unknown[]];

type FbqFunction = ((...args: FbqArguments) => void) & {
  callMethod?: (...args: FbqArguments) => void;
  queue?: FbqArguments[];
  loaded?: boolean;
  version?: string;
  push?: (...args: FbqArguments) => number;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
    __noboriMetaPixelInitialized?: boolean;
  }
}

const META_PIXEL_SCRIPT_ID = "nobori-meta-pixel-script";
const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const META_PIXEL_LEAD_ONCE_PREFIX = "nobori_meta_lead_once_v1";

const getMetaPixelId = () => String(import.meta.env.VITE_META_PIXEL_ID || "").trim();

const createFbqStub = (): FbqFunction => {
  const stub = ((...args: FbqArguments) => {
    if (stub.callMethod) {
      stub.callMethod(...args);
      return;
    }
    stub.queue?.push(args);
  }) as FbqFunction;

  stub.loaded = true;
  stub.version = "2.0";
  stub.queue = [];
  stub.push = (...args: FbqArguments) => stub.queue?.push(args) ?? 0;

  return stub;
};

const ensurePixelScript = () => {
  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SRC;

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }
  document.head.appendChild(script);
};

const ensureFbq = () => {
  const existing = window.fbq;
  if (typeof existing === "function") return existing;

  const fbq = createFbqStub();
  window.fbq = fbq;
  if (!window._fbq) {
    window._fbq = fbq;
  }
  return fbq;
};

export const initMetaPixel = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__noboriMetaPixelInitialized) return;

  const pixelId = getMetaPixelId();
  if (!pixelId) return;

  const fbq = ensureFbq();
  ensurePixelScript();
  fbq("init", pixelId);
  fbq("track", "PageView");
  window.__noboriMetaPixelInitialized = true;
};

export const trackMetaPixelEvent = (
  eventName: string,
  params?: Record<string, unknown>,
) => {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;

  if (params && Object.keys(params).length > 0) {
    fbq("track", eventName, params);
    return;
  }
  fbq("track", eventName);
};

export const trackMetaPixelLeadOnce = (userId?: string | null) => {
  if (typeof window === "undefined") return false;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return false;

  const normalizedUserId = String(userId || "").trim();
  const storageKey = `${META_PIXEL_LEAD_ONCE_PREFIX}:${normalizedUserId || "anonymous"}`;
  if (window.localStorage.getItem(storageKey) === "1") {
    return false;
  }

  fbq("track", "Lead");
  window.localStorage.setItem(storageKey, "1");
  return true;
};
